import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL, ENABLE_DSR } from "../config";
import { apiRequest, formatApiError } from "../apiRequest";
import AgentDsrForm, {
  EMPTY_DSR_FORM,
  buildSuggestedDealTotals,
  buildSuggestedLeadTotals,
  buildSuggestedPipelineTotals
} from "../components/AgentDsrForm";
import DsrReportDetail from "../components/DsrReportDetail";
import DsrReportsWorkspace from "../components/DsrReportsWorkspace";
import { buildDsrSubmissionPayload } from "../components/cpaDsr";

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" }
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 4 }, (_, index) => String(currentYear - 1 + index));
const AGENT_STATUS_REFRESH_MS = 30000;
const BREAK_SUMMARY_REFRESH_MS = 30000;
const AGENT_STATUS_EVENT_KEY = "sales-floor-dashboard:agent-status-changed";

function getPstDateInputValue(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

function broadcastAgentStatusRefresh() {
  try {
    localStorage.setItem(AGENT_STATUS_EVENT_KEY, String(Date.now()));
  } catch {
    // Cross-tab refresh is best effort only.
  }

  window.dispatchEvent(new Event(AGENT_STATUS_EVENT_KEY));
}

function runWhenVisible(callback) {
  if (document.visibilityState === "visible") {
    callback();
  }
}

function AgentPage({ agentName, logout, updateStatus, onOpenKpi, onOpenKpiProfile }) {
  const [, setTick] = useState(0);
  const [agent, setAgent] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [zoomLive, setZoomLive] = useState({
    loading: true,
    error: "",
    configured: false,
    mapped: false,
    availability: null,
    dialStats: null
  });
  const [breakSummary, setBreakSummary] = useState({
    loading: true,
    error: "",
    date: "",
    totalBreakTime: 0,
    currentBreakTime: 0,
    breakCount: 0,
    exceeded: false,
    overageMs: 0,
    breakLimitMs: 60 * 60 * 1000,
    remainingBreakMs: 60 * 60 * 1000,
    isOnBreak: false
  });
  const [arrFilters, setArrFilters] = useState(() => {
    const now = new Date();
    return {
      month: String(now.getMonth() + 1).padStart(2, "0"),
      year: String(now.getFullYear())
    };
  });
  const [arrSummary, setArrSummary] = useState({
    loading: true,
    error: "",
    totalARR: 0,
    outboundARR: 0,
    inboundARR: 0,
    saleCount: 0,
    matchedSheetName: "",
    role: "",
    team: ""
  });
  const [dsrProfile, setDsrProfile] = useState({
    loading: true,
    error: "",
    agentName: agentName,
    businessDate: "",
    dsrRole: "bde",
    minBdeDials: 150,
    existingReport: null,
    pendingApprovalRequest: null,
    leadCloserOptions: [],
    runningTotals: {
      baseNewLeadsMtd: 0,
      baseNewLeadsYtd: 0,
      baseColdLeadsMtd: 0,
      baseWarmLeadsMtd: 0,
      baseHotLeadsMtd: 0,
      baseDealsMtd: 0,
      baseDealsYtd: 0
    }
  });
  const [dsrForm, setDsrForm] = useState(EMPTY_DSR_FORM);
  const [dsrSubmitting, setDsrSubmitting] = useState(false);
  const [dsrMessage, setDsrMessage] = useState("");
  const [teamDsrReports, setTeamDsrReports] = useState([]);
  const [teamDsrSummary, setTeamDsrSummary] = useState({});
  const [teamDsrLoading, setTeamDsrLoading] = useState(false);
  const [teamDsrError, setTeamDsrError] = useState("");
  const [teamDsrApprovals, setTeamDsrApprovals] = useState([]);
  const [teamDsrApprovalsLoading, setTeamDsrApprovalsLoading] = useState(false);
  const [teamDsrApprovalsError, setTeamDsrApprovalsError] = useState("");
  const [reviewingTeamDsrApprovalId, setReviewingTeamDsrApprovalId] = useState("");
  const [selectedTeamDsrReportId, setSelectedTeamDsrReportId] = useState("");
  const [savingTeamDsrReviewId, setSavingTeamDsrReviewId] = useState("");
  const [agentDsrReports, setAgentDsrReports] = useState([]);
  const [agentDsrReportsLoading, setAgentDsrReportsLoading] = useState(false);
  const [agentDsrReportsError, setAgentDsrReportsError] = useState("");
  const [expandedAgentDsrReportId, setExpandedAgentDsrReportId] = useState("");
  const [agentDsrHistoryRange, setAgentDsrHistoryRange] = useState(() => {
    const today = getPstDateInputValue();
    return {
      from: `${today.slice(0, 7)}-01`,
      to: today
    };
  });
  const [teamDsrFilters, setTeamDsrFilters] = useState(() => {
    const today = getPstDateInputValue();
    return {
      from: today,
      to: today,
      role: "all",
      agent: ""
    };
  });
  const [teamMissingDsrResult, setTeamMissingDsrResult] = useState(null);
  const [teamMissingDsrLoading, setTeamMissingDsrLoading] = useState(false);
  const [teamMissingDsrError, setTeamMissingDsrError] = useState("");
  const dsrLeadTotalOverrides = useRef({
    newLeadsMtd: false,
    newLeadsYtd: false,
    coldLeadsMtd: false,
    warmLeadsMtd: false,
    hotLeadsMtd: false,
    dealsMtd: false,
    dealsYtd: false
  });

  const fetchBreakSummary = useCallback(async () => {
    try {
      const { data } = await apiRequest(
        `${API_URL}/agent/break-summary`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          }
        },
        { retries: 2, timeoutMs: 10000 }
      );

      setBreakSummary({
        loading: false,
        error: "",
        date: data.date || "",
        totalBreakTime: data.totalBreakTime || 0,
        currentBreakTime: data.currentBreakTime || 0,
        breakCount: data.breakCount || 0,
        exceeded: Boolean(data.exceeded),
        overageMs: data.overageMs || 0,
        breakLimitMs: data.breakLimitMs || 60 * 60 * 1000,
        remainingBreakMs: data.remainingBreakMs || 0,
        isOnBreak: Boolean(data.isOnBreak)
      });
    } catch (err) {
      if (err.data?.code === "MOBILE_ACCESS_BLOCKED") return;
      if (err.status === 401 || err.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.reload();
        return;
      }

      console.log("Agent break summary fetch error:", err);
      setBreakSummary((current) => ({
        ...current,
        loading: false,
        error: formatApiError(err, "Break summary unavailable")
      }));
    }
  }, []);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const { data } = await apiRequest(
          `${API_URL}/agents?ts=${Date.now()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || ""}`
            }
          },
          { retries: 1, timeoutMs: 10000 }
        );

        if (!Array.isArray(data)) return;

        const currentAgent = data.find((a) => a.name === agentName);
        if (currentAgent) setAgent(currentAgent);
      } catch (err) {
        if (err.data?.code === "MOBILE_ACCESS_BLOCKED") return;
        if (err.status === 401 || err.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.reload();
          return;
        }

        console.log(err);
      }
    };

    fetchAgent();
    const agentInterval = setInterval(() => {
      runWhenVisible(fetchAgent);
    }, AGENT_STATUS_REFRESH_MS);

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => {
      clearInterval(agentInterval);
      clearInterval(interval);
    };
  }, [agentName]);

  useEffect(() => {
    fetchBreakSummary();
    const interval = setInterval(() => {
      runWhenVisible(fetchBreakSummary);
    }, BREAK_SUMMARY_REFRESH_MS);

    return () => clearInterval(interval);
  }, [fetchBreakSummary]);

  useEffect(() => {
    // Zoom live polling is intentionally disabled to reduce Cloudflare request usage.
    setZoomLive((current) => ({ ...current, loading: false, error: "" }));
    return undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchArrSummary = async () => {
      try {
        setArrSummary((current) => ({ ...current, loading: true, error: "" }));
        const params = new URLSearchParams({
          month: arrFilters.month,
          year: arrFilters.year
        });
        const { data } = await apiRequest(
          `${API_URL}/agent/arr-summary?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token") || ""}`
            }
          },
          { retries: 1, timeoutMs: 10000 }
        );

        if (cancelled) return;

        setArrSummary({
          loading: false,
          error: "",
          totalARR: data.totalARR || 0,
          outboundARR: data.outboundARR || 0,
          inboundARR: data.inboundARR || 0,
          saleCount: data.saleCount || 0,
          matchedSheetName: data.matchedSheetName || "",
          role: data.role || "",
          team: data.team || ""
        });
      } catch (err) {
        console.log("Agent ARR summary fetch error:", err);
        if (cancelled) return;

        setArrSummary((current) => ({
          ...current,
          loading: false,
          error: formatApiError(err, "ARR summary unavailable")
        }));
      }
    };

    fetchArrSummary();

    return () => {
      cancelled = true;
    };
  }, [arrFilters.month, arrFilters.year]);

  const fetchDsrProfile = useCallback(async () => {
    if (!ENABLE_DSR) {
      setDsrProfile((current) => ({
        ...current,
        loading: false,
        error: ""
      }));
      return;
    }

    try {
      setDsrProfile((current) => ({ ...current, loading: true, error: "" }));
      const { data } = await apiRequest(
        `${API_URL}/agent/dsr-profile?ts=${Date.now()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          }
        },
        { retries: 2, timeoutMs: 12000 }
      );

      const nextRunningTotals = data.runningTotals || {
        baseNewLeadsMtd: 0,
        baseNewLeadsYtd: 0,
        baseColdLeadsMtd: 0,
        baseWarmLeadsMtd: 0,
        baseHotLeadsMtd: 0,
        baseDealsMtd: 0,
        baseDealsYtd: 0
      };
      const hasLockedReport = Boolean(data.existingReport || data.pendingApprovalRequest);

      setDsrProfile({
        loading: false,
        error: "",
        agentName: data.agentName || agentName,
        businessDate: data.businessDate || "",
        dsrRole: data.dsrRole || "bde",
        minBdeDials: data.minBdeDials || 150,
        existingReport: data.existingReport || null,
        pendingApprovalRequest: data.pendingApprovalRequest || null,
        leadCloserOptions: Array.isArray(data.leadCloserOptions) ? data.leadCloserOptions : [],
        cpAssigneeOptions: Array.isArray(data.cpAssigneeOptions) ? data.cpAssigneeOptions : [],
        runningTotals: nextRunningTotals
      });

      if (!hasLockedReport) {
        dsrLeadTotalOverrides.current = {
          newLeadsMtd: false,
          newLeadsYtd: false,
          coldLeadsMtd: false,
          warmLeadsMtd: false,
          hotLeadsMtd: false,
          dealsMtd: false,
          dealsYtd: false
        };
        setDsrForm({
          ...EMPTY_DSR_FORM,
          ...buildSuggestedLeadTotals(nextRunningTotals, 0, 0),
          ...buildSuggestedDealTotals(nextRunningTotals, 0, 0),
          ...buildSuggestedPipelineTotals(nextRunningTotals, EMPTY_DSR_FORM)
        });
      }
    } catch (err) {
      console.log("DSR profile fetch error:", err);
      setDsrProfile((current) => ({
        ...current,
        loading: false,
        error: formatApiError(err, "DSR profile unavailable")
      }));
    }
  }, [agentName]);

  const fetchAgentDsrReports = useCallback(async () => {
    if (!ENABLE_DSR) return;

    try {
      setAgentDsrReportsLoading(true);
      setAgentDsrReportsError("");
      const params = new URLSearchParams({
        from: agentDsrHistoryRange.from,
        to: agentDsrHistoryRange.to,
        ts: String(Date.now())
      });
      const { data } = await apiRequest(
        `${API_URL}/agent/dsr-reports?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          }
        },
        { retries: 2, timeoutMs: 12000 }
      );

      setAgentDsrReports(Array.isArray(data.reports) ? data.reports : []);
    } catch (err) {
      console.log("Agent DSR history fetch error:", err);
      setAgentDsrReportsError(formatApiError(err, "Failed to load your DSR history"));
    } finally {
      setAgentDsrReportsLoading(false);
    }
  }, [agentDsrHistoryRange.from, agentDsrHistoryRange.to]);

  const fetchTeamDsrApprovals = useCallback(async (overrideFilters = teamDsrFilters) => {
    if (!ENABLE_DSR || dsrProfile.dsrRole !== "tl") return;

    try {
      setTeamDsrApprovalsLoading(true);
      setTeamDsrApprovalsError("");
      const from = overrideFilters.from || getPstDateInputValue();
      const to = overrideFilters.to || from;
      const params = new URLSearchParams({
        from,
        to,
        status: "pending",
        ts: String(Date.now())
      });

      if (overrideFilters.role && overrideFilters.role !== "all") params.append("role", overrideFilters.role);
      if (overrideFilters.agent) params.append("agent", overrideFilters.agent);

      const { data } = await apiRequest(
        `${API_URL}/dsr-approval-requests?${params.toString()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } },
        { retries: 2, timeoutMs: 12000 }
      );
      setTeamDsrApprovals(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      console.log("Team DSR approval fetch error:", err);
      setTeamDsrApprovalsError(formatApiError(err, "Failed to load team approval requests"));
    } finally {
      setTeamDsrApprovalsLoading(false);
    }
  }, [dsrProfile.dsrRole, teamDsrFilters]);

  const fetchTeamDsrReports = useCallback(async (overrideFilters = teamDsrFilters) => {
    if (!ENABLE_DSR || dsrProfile.dsrRole !== "tl") return;

    try {
      setTeamDsrLoading(true);
      setTeamDsrError("");
      const from = overrideFilters.from || getPstDateInputValue();
      const to = overrideFilters.to || from;
      const params = new URLSearchParams({
        from,
        to,
        ts: String(Date.now())
      });

      if (overrideFilters.role && overrideFilters.role !== "all") params.append("role", overrideFilters.role);
      if (overrideFilters.agent) params.append("agent", overrideFilters.agent);

      const { data } = await apiRequest(
        `${API_URL}/dsr-reports?${params.toString()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } },
        { retries: 2, timeoutMs: 12000 }
      );

      setTeamDsrReports(Array.isArray(data.reports) ? data.reports : []);
      setTeamDsrSummary(data.summary || {});
      setTeamDsrFilters((current) => ({
        ...current,
        from: data.from || from,
        to: data.to || to
      }));
      fetchTeamDsrApprovals({ ...overrideFilters, from: data.from || from, to: data.to || to });
    } catch (err) {
      console.log("Team DSR report fetch error:", err);
      setTeamDsrError(formatApiError(err, "Failed to load team DSR reports"));
    } finally {
      setTeamDsrLoading(false);
    }
  }, [dsrProfile.dsrRole, fetchTeamDsrApprovals, teamDsrFilters]);

  const fetchTeamMissingDsrAgents = useCallback(async (override = {}) => {
    if (!ENABLE_DSR || dsrProfile.dsrRole !== "tl") return;

    try {
      setTeamMissingDsrLoading(true);
      setTeamMissingDsrError("");
      const selected = { ...teamDsrFilters, ...override };
      const role = selected.role || "all";
      const params = new URLSearchParams({ from: selected.from, to: selected.to, ts: String(Date.now()) });
      if (role && role !== "all") params.append("role", role);
      if (selected.agent) params.append("agent", selected.agent);

      const { data } = await apiRequest(
        `${API_URL}/dsr-missing?${params.toString()}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } },
        { retries: 2, timeoutMs: 12000 }
      );
      setTeamMissingDsrResult(data);
    } catch (err) {
      console.log("Team missing DSR fetch error:", err);
      setTeamMissingDsrError(formatApiError(err, "Failed to load team missing DSR"));
    } finally {
      setTeamMissingDsrLoading(false);
    }
  }, [dsrProfile.dsrRole, teamDsrFilters]);

  const resetTeamDsrFilters = useCallback(() => {
    const today = getPstDateInputValue();
    const nextFilters = { from: today, to: today, role: "all", agent: "" };
    setTeamDsrFilters(nextFilters);
    setSelectedTeamDsrReportId("");
    fetchTeamDsrReports(nextFilters);
  }, [fetchTeamDsrReports]);

  const [showTeamDsrWorkspace, setShowTeamDsrWorkspace] = useState(true);

  const saveTeamDsrReview = async (report, reviewFields) => {
    if (!report?.id || savingTeamDsrReviewId) return null;

    try {
      setSavingTeamDsrReviewId(report.id);
      setTeamDsrError("");
      const { data } = await apiRequest(
        `${API_URL}/dsr-reports/${report.id}/manager-review`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          },
          body: JSON.stringify(reviewFields || {})
        },
        { retries: 1, timeoutMs: 12000 }
      );

      const savedReview = data.review || null;
      setTeamDsrReports((current) => current.map((item) =>
        item.id === report.id
          ? { ...item, managerReview: savedReview, managerReviewStatus: "reviewed", managerReviewFollowUpDate: savedReview?.followUpDate || "" }
          : item
      ));
      await fetchTeamDsrReports();
      return savedReview;
    } catch (err) {
      setTeamDsrError(formatApiError(err, "Failed to save TL review"));
      throw err;
    } finally {
      setSavingTeamDsrReviewId("");
    }
  };

  const reviewTeamDsrApprovalRequest = async (requestId, action, note = "") => {
    if (!requestId || reviewingTeamDsrApprovalId) return;

    try {
      setReviewingTeamDsrApprovalId(requestId);
      setTeamDsrApprovalsError("");
      await apiRequest(
        `${API_URL}/dsr-approval-requests/${requestId}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          },
          body: JSON.stringify({ note })
        },
        { retries: 1, timeoutMs: 12000 }
      );
      await Promise.all([
        fetchTeamDsrApprovals(),
        fetchTeamDsrReports(),
        teamMissingDsrResult ? fetchTeamMissingDsrAgents() : Promise.resolve()
      ]);
    } catch (err) {
      setTeamDsrApprovalsError(formatApiError(err, `Failed to ${action} DSR approval`));
    } finally {
      setReviewingTeamDsrApprovalId("");
    }
  };

  useEffect(() => {
    fetchDsrProfile();
  }, [fetchDsrProfile]);

  useEffect(() => {
    fetchAgentDsrReports();
  }, [fetchAgentDsrReports]);

  useEffect(() => {
    if (dsrProfile.dsrRole === "tl") {
      fetchTeamDsrReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsrProfile.dsrRole]);

  const formatTime = (ms) => {
    if (!ms || ms < 0) return "0m 0s";

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }

    return `${minutes}m ${seconds}s`;
  };

  const formatCurrency = (amount) =>
    `$${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}`;

  const updateDsrField = (field, value) => {
    if (
      field === "newLeadsMtd" ||
      field === "newLeadsYtd" ||
      field === "coldLeadsMtd" ||
      field === "warmLeadsMtd" ||
      field === "hotLeadsMtd" ||
      field === "dealsMtd" ||
      field === "dealsYtd"
    ) {
      dsrLeadTotalOverrides.current[field] = true;
    }

    setDsrForm((current) => {
      const nextForm = { ...current, [field]: value };

      if (field === "newLeads" || field === "leadLost") {
        const suggestedTotals = buildSuggestedLeadTotals(
          dsrProfile.runningTotals,
          field === "newLeads" ? value : nextForm.newLeads,
          field === "leadLost" ? value : nextForm.leadLost
        );
        if (!dsrLeadTotalOverrides.current.newLeadsMtd) {
          nextForm.newLeadsMtd = suggestedTotals.newLeadsMtd;
        }
        if (!dsrLeadTotalOverrides.current.newLeadsYtd) {
          nextForm.newLeadsYtd = suggestedTotals.newLeadsYtd;
        }
      }

      if (field === "dealsClosed" || field === "dealLost") {
        const suggestedDealTotals = buildSuggestedDealTotals(
          dsrProfile.runningTotals,
          field === "dealsClosed" ? value : nextForm.dealsClosed,
          field === "dealLost" ? value : nextForm.dealLost
        );
        if (!dsrLeadTotalOverrides.current.dealsMtd) {
          nextForm.dealsMtd = suggestedDealTotals.dealsMtd;
        }
        if (!dsrLeadTotalOverrides.current.dealsYtd) {
          nextForm.dealsYtd = suggestedDealTotals.dealsYtd;
        }
      }

      if (field === "coldLeadsToday" || field === "warmLeadsToday" || field === "hotLeadsToday") {
        const suggestedPipeline = buildSuggestedPipelineTotals(dsrProfile.runningTotals, nextForm);
        if (!dsrLeadTotalOverrides.current.coldLeadsMtd) {
          nextForm.coldLeadsMtd = suggestedPipeline.coldLeadsMtd;
        }
        if (!dsrLeadTotalOverrides.current.warmLeadsMtd) {
          nextForm.warmLeadsMtd = suggestedPipeline.warmLeadsMtd;
        }
        if (!dsrLeadTotalOverrides.current.hotLeadsMtd) {
          nextForm.hotLeadsMtd = suggestedPipeline.hotLeadsMtd;
        }
      }

      return nextForm;
    });
    setDsrMessage("");
  };

  const submitDsr = async (event) => {
    event?.preventDefault?.();
    setDsrMessage("");

    if (!ENABLE_DSR) {
      setDsrMessage("DSR is not enabled yet.");
      return;
    }

    try {
      setDsrSubmitting(true);
      const { data } = await apiRequest(
        `${API_URL}/agent/dsr`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`
          },
          body: JSON.stringify(buildDsrSubmissionPayload(dsrProfile.dsrRole, dsrForm))
        },
        { retries: 2, timeoutMs: 15000 }
      );

      setDsrMessage(data.approvalRequired
        ? "Your dials are below 150 and no sale was recorded. TL approval is required before this DSR can be accepted."
        : "DSR submitted successfully.");
      dsrLeadTotalOverrides.current = {
        newLeadsMtd: false,
        newLeadsYtd: false,
        coldLeadsMtd: false,
        warmLeadsMtd: false,
        hotLeadsMtd: false,
        dealsMtd: false,
        dealsYtd: false
      };
      setDsrForm(EMPTY_DSR_FORM);
      fetchDsrProfile();
      fetchAgentDsrReports();
    } catch (err) {
      console.log("DSR submit error:", err);
      setDsrMessage(formatApiError(err, "Failed to submit DSR"));
    } finally {
      setDsrSubmitting(false);
    }
  };

 const handleStatusUpdate = async (status, breakType = null) => {
if (status === "Break" && agent?.status === "Break") {
  alert("You are already on break");
  return;
}

  if (status === "Meeting" && agent?.status === "Meeting") {
    alert("You are already in meeting");
    return;
  }

  const previousAgent = agent;
  const previousBreakSummary = breakSummary;

  try {
    setUpdating(true);

    const now = Date.now();
    const wasOnBreak = agent?.status === "Break";
    const breakStartTime = Number(agent?.statusStartTime || 0);
    const completedBreakTime =
      wasOnBreak && status !== "Break" && breakStartTime
        ? Math.max(0, now - breakStartTime)
        : 0;
    const nextDailyBreakTime =
      Number(agent?.dailyBreakTime || 0) + completedBreakTime;

    if (agent) {
      setAgent({
        ...agent,
        status,
        breakType: status === "Break" ? breakType : null,
        statusStartTime: ["Break", "Meeting", "Available"].includes(status)
          ? now
          : null,
        dailyBreakTime: nextDailyBreakTime
      });
    }

    setBreakSummary((current) => ({
      ...current,
      loading: false,
      isOnBreak: status === "Break",
      currentBreakTime: 0,
      totalBreakTime:
        status === "Break"
          ? Number(current.totalBreakTime || 0)
          : Math.max(Number(current.totalBreakTime || 0), nextDailyBreakTime)
    }));
    broadcastAgentStatusRefresh();

    const updatedAgent = await updateStatus(status, breakType);

    if (updatedAgent) {
      setAgent(updatedAgent);
    }

    await fetchBreakSummary();
    broadcastAgentStatusRefresh();
  } catch (err) {
    console.log(err);
    if (previousAgent) setAgent(previousAgent);
    if (previousBreakSummary) setBreakSummary(previousBreakSummary);
    alert(formatApiError(err, "Failed to update status"));
  } finally {
    setUpdating(false);
  }
};

const localCurrentBreakTime =
  agent?.status === "Break" && agent?.statusStartTime
    ? Date.now() - Number(agent.statusStartTime)
    : 0;
const currentBreakTime = Math.max(
  localCurrentBreakTime,
  breakSummary.isOnBreak ? Number(breakSummary.currentBreakTime || 0) : 0
);

  const totalBreakToday = Math.max(
    (agent?.dailyBreakTime || 0) + currentBreakTime,
    Number(breakSummary.totalBreakTime || 0)
  );

  const dailyBreakLimitMs = Number(breakSummary.breakLimitMs || 60 * 60 * 1000);
  const remainingBreakMs = Math.max(0, dailyBreakLimitMs - totalBreakToday);
  const zoomAvailability = zoomLive.availability;
  const displayStatus =
    breakSummary.isOnBreak
      ? "Break"
      :
    agent?.status === "Available" && zoomAvailability?.status === "On Call"
      ? "On Call"
      : agent?.status || "Available";
  const statusColor =
    displayStatus === "Break"
      ? "#f59e0b"
      : displayStatus === "On Call"
      ? "#3b82f6"
      : "#22c55e";
  const zoomDialStats = zoomLive.dialStats || {};
  const zoomStatusLabel = zoomLive.loading
    ? "Syncing..."
    : zoomLive.error
    ? "Zoom unavailable"
    : !zoomLive.mapped
    ? "Not linked"
    : zoomAvailability?.statusLabel || zoomAvailability?.status || "Unavailable";
  const zoomStatusClass =
    zoomAvailability?.status === "On Call"
      ? "on-call"
      : zoomAvailability?.status === "Available"
      ? "available"
      : zoomAvailability?.status === "Break"
      ? "break"
      : "offline";
  const zoomStatusStartTime =
    zoomAvailability?.statusStartTime || zoomDialStats.lastStatusStartedAt;
  const zoomStatusTime = zoomStatusStartTime
    ? formatTime(Date.now() - Number(zoomStatusStartTime))
    : "0m 0s";
  return (
    <div className="agent-portal">
      <div
        style={{
          background:
            "linear-gradient(135deg, rgba(37,99,235,0.18), rgba(15,23,42,0.95))",
          border: "1px solid rgba(148,163,184,0.18)",
          borderRadius: "28px",
          padding: "28px",
          marginBottom: "28px",
          boxShadow: "0 14px 32px rgba(0,0,0,0.28)"
        }}
      >
        <div className="agent-header" style={{ marginBottom: 0 }}>
          <div>
            <h1>Agent Portal</h1>
            <p>Live Zoom status, breaks, and daily activity</p>
          </div>

          <div className="agent-user-box">
            <div
              style={{
                textAlign: "right"
              }}
            >
              <strong
                style={{
                  display: "block",
                  color: "#fff",
                  fontSize: "22px"
                }}
              >
                {agentName}
              </strong>

              <span
                style={{
                  display: "inline-block",
                  marginTop: "8px",
                  background: statusColor,
                  color: "#fff",
                  padding: "6px 12px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: "bold"
                }}
              >
                {displayStatus}
              </span>
            </div>

            {onOpenKpi && <button className="kpi-team-lead-link" type="button" onClick={onOpenKpi}>TL Feedback</button>}
            {onOpenKpiProfile && <button className="kpi-team-lead-link" type="button" onClick={onOpenKpiProfile}>My KPI</button>}
            <button className="logout-btn" onClick={logout}>Logout</button>
          </div>
        </div>
      </div>

      <div className="agent-info-grid">
        <div className="agent-info-card">
          <h3>Current Status</h3>
          <p style={{ color: statusColor }}>{displayStatus}</p>
        </div>

        <div className="agent-info-card">
          <h3>Total Break Today</h3>
          <p>{formatTime(totalBreakToday)}</p>
          {breakSummary.breakCount > 0 && (
            <div className="agent-card-subtext">
              <span>
                {breakSummary.breakCount} break
                {breakSummary.breakCount === 1 ? "" : "s"} today
              </span>
              <span>Limit {formatTime(dailyBreakLimitMs)}</span>
              {totalBreakToday > dailyBreakLimitMs ? (
                <span>Exceeded by {formatTime(totalBreakToday - dailyBreakLimitMs)}</span>
              ) : (
                <span>Remaining {formatTime(remainingBreakMs)}</span>
              )}
            </div>
          )}
          {breakSummary.error && (
            <small className="agent-card-error">{breakSummary.error}</small>
          )}
        </div>

        <div className="agent-info-card">
          <h3>Current Break</h3>
          <p>
            {agent?.status === "Break" || breakSummary.isOnBreak
              ? formatTime(currentBreakTime)
              : "0m 0s"}
          </p>
        </div>

        <div className="agent-info-card">
          <h3>Zoom Live</h3>
          <p className={`agent-zoom-state ${zoomStatusClass}`}>
            {zoomStatusLabel}
          </p>
          <div className="agent-card-subtext">
            <span>
              Dials today <b>{zoomDialStats.totalCalls || 0}</b>
            </span>
            <span>Timer {zoomStatusTime}</span>
          </div>
          {zoomLive.error && (
            <small className="agent-card-error">{zoomLive.error}</small>
          )}
        </div>
      </div>

      <div className="agent-action-panel">
        <div className="agent-action-heading">
          <div>
            <h2>Break Actions</h2>
            <p>Update your live status before stepping away.</p>
          </div>

          <span>{updating ? "Updating..." : "Ready"}</span>
        </div>

        <div className="break-actions">
          <button
            className="break-btn bio"
            disabled={updating || agent?.status === "Break"}
            onClick={() => handleStatusUpdate("Break", "Bio Break")}
          >
            Bio Break
          </button>

          <button
            className="break-btn main"
            disabled={updating || agent?.status === "Break"}
            onClick={() => handleStatusUpdate("Break", "Main Break")}
          >
            Main Break
          </button>

          <button
            className="break-btn meeting"
            disabled={updating || agent?.status === "Meeting"}
            onClick={() => handleStatusUpdate("Meeting", null)}
          >
            Meeting
          </button>

          <button
            className="break-btn end"
            disabled={updating}
            onClick={() => handleStatusUpdate("Available", null)}
          >
            Back Available
          </button>
        </div>

        {updating && <p className="agent-action-status">Updating status...</p>}
      </div>

      <div className="agent-arr-panel">
        <div className="agent-arr-heading">
          <div>
            <span>ARR Performance</span>
            <h2>{agentName}</h2>
            <p>
              {arrSummary.loading
                ? "Loading your ARR..."
                : arrSummary.saleCount > 0
                ? `${arrSummary.saleCount} sale${arrSummary.saleCount === 1 ? "" : "s"} found${
                    arrSummary.matchedSheetName && arrSummary.matchedSheetName !== agentName
                      ? ` as ${arrSummary.matchedSheetName}`
                      : ""
                  }`
                : "No ARR found for this period"}
            </p>
          </div>

          <div className="agent-arr-filters">
            <select
              value={arrFilters.month}
              onChange={(event) =>
                setArrFilters((current) => ({ ...current, month: event.target.value }))
              }
            >
              {MONTH_OPTIONS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>

            <select
              value={arrFilters.year}
              onChange={(event) =>
                setArrFilters((current) => ({ ...current, year: event.target.value }))
              }
            >
              {YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {arrSummary.error && <div className="agent-arr-error">{arrSummary.error}</div>}

        <div className="agent-arr-grid">
          <div className="agent-arr-card total">
            <span>Total ARR</span>
            <strong>{formatCurrency(arrSummary.totalARR)}</strong>
          </div>
          <div className="agent-arr-card outbound">
            <span>Outbound ARR</span>
            <strong>{formatCurrency(arrSummary.outboundARR)}</strong>
          </div>
          <div className="agent-arr-card inbound">
            <span>Inbound ARR</span>
            <strong>{formatCurrency(arrSummary.inboundARR)}</strong>
          </div>
        </div>
      </div>

      {ENABLE_DSR && dsrProfile.dsrRole === "tl" && (
        <>
          <AgentDsrForm
            profile={dsrProfile}
            form={dsrForm}
            onChange={updateDsrField}
            onSubmit={submitDsr}
            submitting={dsrSubmitting}
            message={dsrMessage}
          />

          <button type="button" onClick={() => setShowTeamDsrWorkspace(true)}>Team DSR Reviews</button>
          {showTeamDsrWorkspace && <DsrReportsWorkspace
            reports={teamDsrReports}
            summary={teamDsrSummary}
            filters={teamDsrFilters}
            setFilters={setTeamDsrFilters}
            loading={teamDsrLoading}
            error={teamDsrError}
            onClose={() => setShowTeamDsrWorkspace(false)}
            onFetchReports={fetchTeamDsrReports}
            onResetFilters={resetTeamDsrFilters}
            onDownloadCsv={() => {}}
            approvals={teamDsrApprovals}
            approvalsLoading={teamDsrApprovalsLoading}
            approvalsError={teamDsrApprovalsError}
            reviewingApprovalId={reviewingTeamDsrApprovalId}
            onReviewApproval={reviewTeamDsrApprovalRequest}
            missingResult={teamMissingDsrResult}
            missingLoading={teamMissingDsrLoading}
            missingError={teamMissingDsrError}
            onFetchMissing={fetchTeamMissingDsrAgents}
            onDownloadMissingCsv={() => {}}
            selectedReportId={selectedTeamDsrReportId}
            setSelectedReportId={setSelectedTeamDsrReportId}
            deletingReportId=""
            onDeleteReport={() => {}}
            onSaveManagerReview={saveTeamDsrReview}
            savingManagerReviewId={savingTeamDsrReviewId}
            canDeleteReports={false}
            canManageReviews
            workspaceMode="tl"
          />}
        </>
      )}

      {ENABLE_DSR && dsrProfile.dsrRole !== "tl" && (
        <>
          <AgentDsrForm
            profile={dsrProfile}
            form={dsrForm}
            onChange={updateDsrField}
            onSubmit={submitDsr}
            submitting={dsrSubmitting}
            message={dsrMessage}
          />

          <section className="agent-dsr-history">
            <div className="agent-dsr-history-heading">
              <div>
                <span>Submitted DSRs</span>
                <h2>My DSR History</h2>
                <p>Only your own DSRs are shown here.</p>
              </div>
              <div className="agent-dsr-history-controls">
                <label>
                  From
                  <input
                    type="date"
                    value={agentDsrHistoryRange.from}
                    onChange={(event) => setAgentDsrHistoryRange((current) => ({
                      ...current,
                      from: event.target.value
                    }))}
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    value={agentDsrHistoryRange.to}
                    onChange={(event) => setAgentDsrHistoryRange((current) => ({
                      ...current,
                      to: event.target.value
                    }))}
                  />
                </label>
                <button type="button" onClick={fetchAgentDsrReports} disabled={agentDsrReportsLoading}>
                  {agentDsrReportsLoading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>

            {agentDsrReportsError && (
              <div className="agent-dsr-message error">{agentDsrReportsError}</div>
            )}

            {agentDsrReportsLoading ? (
              <p className="agent-dsr-history-empty">Loading your DSRs...</p>
            ) : agentDsrReports.length === 0 ? (
              <p className="agent-dsr-history-empty">No submitted DSRs found yet.</p>
            ) : (
              <div className="agent-dsr-history-list">
                {agentDsrReports.slice(0, 10).map((report) => (
                  <div key={report.id} className="agent-dsr-history-row">
                    <div>
                      <strong>{report.businessDate}</strong>
                      <span>{String(report.dsrRole || "").toUpperCase()} • Submitted {report.submittedAt ? new Date(report.submittedAt).toLocaleString() : "-"}</span>
                    </div>
                    <div className="agent-dsr-history-metrics">
                      <span>{report.dials || 0} calls</span>
                      <span>{report.newLeads || 0} leads</span>
                      <button type="button" onClick={() => setExpandedAgentDsrReportId(report.id)}>
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <DsrReportDetail
            report={agentDsrReports.find((report) => report.id === expandedAgentDsrReportId) || null}
            onClose={() => setExpandedAgentDsrReportId("")}
          />
        </>
      )}
    </div>
  );
}

export default AgentPage;
